import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';
import { UpdateService } from '../../services/UpdateService';
import './UpdateNotification.scss';

const updateService = new UpdateService();

// Definición de variantes para animaciones
const VARIANTS_OVERLAY = {
	open: {
		opacity: 1,
		transition: {
			duration: 0.3,
		},
	},
	closed: {
		opacity: 0,
		transition: {
			duration: 0.3,
		},
	},
};

const VARIANTS_MODAL = {
	open: {
		opacity: 1,
		scale: 1,
		y: 0,
		transition: {
			type: 'spring',
			stiffness: 400,
			damping: 30,
		},
	},
	closed: {
		opacity: 0,
		scale: 0.8,
		y: 20,
		transition: {
			duration: 0.3,
		},
	},
};

const VARIANTS_ICON = {
	open: {
		rotate: 0,
		scale: 1,
		transition: {
			type: 'spring',
			stiffness: 300,
			damping: 20,
		},
	},
	closed: {
		rotate: -180,
		scale: 0,
		transition: {
			duration: 0.2,
		},
	},
};

export const UpdateNotification = (): JSX.Element | null => {
	const { t } = useTranslation();
	const [showNotification, setShowNotification] = useState(false);

	useEffect(() => {
		const subscription = updateService
			.getUpdateAvailable()
			.subscribe(isAvailable => {
				setShowNotification(isAvailable);
			});

		return () => {
			subscription.unsubscribe();
		};
	}, []);

	const handleUpdate = async (): Promise<void> => {
		try {
			setShowNotification(false);
			await updateService.forceUpdate();
		} catch (error) {
			console.error('Error al actualizar:', error);
			setShowNotification(true);
		}
	};

	// Handler síncrono para el evento onClick
	const onClickHandler = (): void => {
		handleUpdate().catch(error => {
			console.error('Error en handleUpdate:', error);
		});
	};

	return (
		<AnimatePresence>
			{showNotification && (
				<motion.div
					className='update-notification-overlay'
					initial='closed'
					animate='open'
					exit='closed'
					variants={VARIANTS_OVERLAY}
				>
					<motion.div
						className='update-notification-modal'
						variants={VARIANTS_MODAL}
					>
						<motion.div
							className='update-notification-icon-container'
							variants={VARIANTS_ICON}
						>
							<div className='update-notification-icon'>
								<svg
									xmlns='http://www.w3.org/2000/svg'
									width='24'
									height='24'
									viewBox='0 0 24 24'
									fill='none'
								>
									<path
										d='M6.48959 17.5104C3.17014 14.191 3.17014 8.80905 6.48959 5.48959C8.58797 3.39121 11.5105 2.61932 14.2152 3.1739M19.3525 6.46871C21.801 9.79488 21.5203 14.5005 18.5104 17.5104C16.3451 19.6757 13.3022 20.4286 10.5267 19.769M17.6684 7.83484V4.64734L20.8559 4.64734L17.6684 7.83484ZM7.24035 15.0742V18.2617H4.05285L7.24035 15.0742Z'
										stroke='#364F6D'
										strokeWidth='2'
										strokeLinecap='round'
										strokeLinejoin='round'
									/>
								</svg>
							</div>
						</motion.div>

						<motion.div className={'center-mod-container'}>
							<h2 className='update-notification-title'>
								{t('REFRESH_DEPLOY_TITLE')}
							</h2>

							<p className='update-notification-message'>
								{t('REFRESH_DEPLOY_INFO')}
							</p>
						</motion.div>

						<motion.div className={'update-notification-button-container'}>
							<motion.button
								className='update-notification-button'
								onClick={onClickHandler}
								whileHover={{ scale: 1.05 }}
								whileTap={{ scale: 0.95 }}
							>
								{t('REFRESH_DEPLOY_ACTION')}
							</motion.button>
						</motion.div>
					</motion.div>
				</motion.div>
			)}
		</AnimatePresence>
	);
};
